import { useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, ChevronDown, ChevronRight, Mail } from "lucide-react";
import * as adminApi from "@/api/admin";
import type { ContactMessage } from "@/api/admin";

export function MessagesView({ source }: { source?: string }) {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(false);
    adminApi.getMessages(source)
      .then(setMessages)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [source]);

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load messages. Please refresh and try again.</AlertDescription>
      </Alert>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400">
        <Mail size={36} className="mb-3 opacity-30" />
        <p className="text-sm">No messages yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {messages.map((msg) => {
        const isOpen = expanded === msg.id;
        const date = new Date(msg.created_at).toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        });
        return (
          <div
            key={msg.id}
            className="border border-gray-200 rounded-lg overflow-hidden bg-white"
          >
            <button
              onClick={() => setExpanded(isOpen ? null : msg.id)}
              className="w-full flex items-center gap-4 px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
            >
              <span className="shrink-0 text-gray-400">
                {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-sm text-gray-900 truncate">{msg.name}</span>
                  <span className="text-gray-300 text-xs shrink-0">·</span>
                  <span className="text-xs text-gray-500 truncate">{msg.email}</span>
                </div>
                {msg.subject && (
                  <p className="text-xs text-gray-400 truncate mt-0.5">{msg.subject}</p>
                )}
              </div>
              <span className="text-xs text-gray-400 shrink-0">{date}</span>
            </button>

            {isOpen && (
              <div className="border-t border-gray-100 px-5 py-4 space-y-3 bg-gray-50/50">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <div>
                    <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Name</span>
                    <p className="text-gray-800 mt-0.5">{msg.name}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Email</span>
                    <p className="text-gray-800 mt-0.5">
                      <a href={`mailto:${msg.email}`} className="hover:underline">{msg.email}</a>
                    </p>
                  </div>
                  {msg.phone && (
                    <div>
                      <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Phone</span>
                      <p className="text-gray-800 mt-0.5">{msg.phone}</p>
                    </div>
                  )}
                  {msg.company && (
                    <div>
                      <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Company</span>
                      <p className="text-gray-800 mt-0.5">{msg.company}</p>
                    </div>
                  )}
                  {msg.subject && (
                    <div className="col-span-2">
                      <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Subject</span>
                      <p className="text-gray-800 mt-0.5">{msg.subject}</p>
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Message</span>
                  <p className="text-gray-800 text-sm mt-1 whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
